import React, { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FiCheck, FiFileText, FiLoader, FiTrash2, FiUpload } from 'react-icons/fi';
import Button from '../../../shared/components/ui/Button';
import {
  draftLandFromDocument,
  draftLandFromDescription
} from '../../services/api/implementation/impl';
import {
  LAND_SOURCE_DOCUMENTS,
  LAND_DOCUMENT_LABELS,
  LAND_DRAFT_FIELD_LABELS
} from '../../constants/land';

/**
 * Smart Fill for land onboarding — AI draft extraction, reviewed before it touches the form.
 *
 * WHY THIS HAS A REVIEW STEP AND THE COMPANY PANEL DOES NOT: a company draft is a handful
 * of scalar fields, each visible in the form the moment it lands. A land draft brings rows
 * — parcels and owners — that would otherwise appear as new blocks in a long form with no
 * indication of where they came from or how confident the model was. So nothing is applied
 * until the operator has looked at the table and pressed Apply.
 *
 * ON COVERAGE: registry deeds are long. The backend reads selected passages of a text PDF
 * or the first few pages of a scan, and reports which. That is shown, not hidden — on a
 * 40-page deed, "no parcels found" means something very different depending on whether the
 * parcel schedule was in the part that was read.
 *
 * ON VERIFICATION: nothing here is verified. There is no registry lookup, by decision.
 * These values were transcribed from a document by a model, which is evidence of
 * transcription and nothing more. No green "Verified" badge appears anywhere in this
 * component, and the confirm button says Apply, not Verify.
 */

const STEPS = [
  { key: 'send', label: 'Reading your document' },
  { key: 'extract', label: 'Extracting parcels, owners and areas' },
  { key: 'review', label: 'Ready for your review' }
];

/** Model confidence → how much the row should draw the eye. */
const confidenceTone = (confidence) => {
  if (confidence >= 0.8) return 'text-emerald-700';
  if (confidence >= 0.5) return 'text-amber-700';
  return 'text-red-700';
};

const CoverageNote = ({ coverage }) => {
  if (!coverage || !coverage.truncated) return null;

  const message =
    coverage.pagesTotal != null
      ? `Only pages 1–${coverage.pagesRead} of ${coverage.pagesTotal} were read.`
      : `Only the passages most likely to hold land details were read (${Math.round(
          (coverage.charsRead / Math.max(coverage.charsTotal, 1)) * 100
        )}% of the document).`;

  return (
    <p className="mt-2 rounded border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
      {message} Anything recorded elsewhere in the document will be missing — add it by
      hand, or upload the relevant pages on their own.
    </p>
  );
};

/**
 * Parcel review grid.
 *
 * Every row is editable and removable before it reaches the form, because an extracted
 * khasra number is a claim about a title, not a fact. Conflicts between two documents are
 * shown inline rather than resolved — that is a question for a human.
 */
const KhasraReviewGrid = ({ rows, onChange }) => {
  if (!rows.length) return null;

  const update = (index, key, value) =>
    onChange(rows.map((row, position) => (position === index ? { ...row, [key]: value } : row)));

  const remove = (index) => onChange(rows.filter((_, position) => position !== index));

  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full min-w-[640px] text-left text-xs">
        <thead>
          <tr className="text-stone-500">
            <th className="pb-1 pr-2 font-normal">Khasra / Survey no.</th>
            <th className="pb-1 pr-2 font-normal">Main area</th>
            <th className="pb-1 pr-2 font-normal">Unit</th>
            <th className="pb-1 pr-2 font-normal">Owner</th>
            <th className="pb-1 pr-2 font-normal">Read from</th>
            <th className="pb-1" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${row.number}-${index}`} className="border-t border-stone-200">
              <td className="py-1.5 pr-2">
                <input
                  className="w-28 rounded border border-stone-300 px-1.5 py-1"
                  value={row.number || ''}
                  onChange={(event) => update(index, 'number', event.target.value)}
                />
              </td>
              <td className="py-1.5 pr-2">
                <input
                  className="w-24 rounded border border-stone-300 px-1.5 py-1"
                  value={row.main_area || ''}
                  onChange={(event) => update(index, 'main_area', event.target.value)}
                />
              </td>
              <td className="py-1.5 pr-2 text-stone-600">{row.main_area_unit || '—'}</td>
              <td className="py-1.5 pr-2 text-stone-600">
                {row.owner_name || row.owner_pan || '—'}
              </td>
              <td className={`py-1.5 pr-2 ${confidenceTone(row.confidence ?? 0)}`}>
                {LAND_DOCUMENT_LABELS[row.source] || row.source}
                {row.confidence != null && ` · ${Math.round(row.confidence * 100)}%`}
              </td>
              <td className="py-1.5 text-right">
                <button
                  type="button"
                  aria-label={`Remove parcel ${row.number}`}
                  className="text-stone-400 hover:text-red-600"
                  onClick={() => remove(index)}>
                  <FiTrash2 />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {rows.some((row) => (row.conflicts || []).length > 0) && (
        <div className="mt-2 rounded border border-amber-300 bg-amber-50 px-3 py-2">
          {rows.flatMap((row) =>
            (row.conflicts || []).map((conflict) => (
              <p
                key={`${row.number}-${conflict.attribute}-${conflict.value}`}
                className="text-xs text-amber-800">
                Khasra {row.number}: {conflict.attribute.replace(/_/g, ' ')} reads &ldquo;
                {conflict.value}&rdquo; in the{' '}
                {LAND_DOCUMENT_LABELS[conflict.source] || conflict.source}. Confirm which
                document is right.
              </p>
            ))
          )}
        </div>
      )}

      {rows.some((row) => row.evidence) && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs text-stone-500">
            Show the quote each parcel was read from
          </summary>
          <ul className="mt-1 flex flex-col gap-1">
            {rows
              .filter((row) => row.evidence)
              .map((row, index) => (
                <li key={`${row.number}-evidence-${index}`} className="text-xs text-stone-600">
                  <span className="text-stone-400">{row.number}:</span> &ldquo;{row.evidence}
                  &rdquo;
                </li>
              ))}
          </ul>
        </details>
      )}
    </div>
  );
};

const LandSmartFillPanel = ({ onApplyDraft, disabled }) => {
  const [description, setDescription] = useState('');
  const [stage, setStage] = useState(null); // null | 'send' | 'extract' | 'review'
  const [lastResult, setLastResult] = useState(null);
  const [khasraRows, setKhasraRows] = useState([]);

  const isBusy = stage === 'send' || stage === 'extract';
  const draft = lastResult?.draft;

  const fieldEntries = useMemo(
    () =>
      Object.entries(draft?.fields || {}).filter(
        ([name]) => LAND_DRAFT_FIELD_LABELS[name] != null
      ),
    [draft]
  );

  const reset = useCallback(() => {
    setStage(null);
    setLastResult(null);
    setKhasraRows([]);
  }, []);

  const handleResult = useCallback((result) => {
    const fields = result?.draft?.fields || {};
    const parcels = result?.draft?.khasra || [];
    const owners = result?.draft?.owners || [];
    const found = Object.keys(fields).length + parcels.length + owners.length;

    if (result?.mode === 'disabled') {
      toast('AI assist is switched off — please enter the details manually.', { icon: 'ℹ️' });
    } else if (result?.mode === 'failed') {
      // Say why. Reporting a credits or timeout failure as "switched off" sends people to
      // check env vars that are already correct.
      toast.error(result.error || 'Extraction failed — please enter the details manually.');
    } else if (found === 0) {
      toast('Nothing could be read from that document. Please enter the details manually.');
    } else {
      toast.success(`${found} item${found === 1 ? '' : 's'} read — check each one before applying.`);
    }

    setKhasraRows(parcels);
    setLastResult(result);
    setStage('review');
  }, []);

  const handleFailure = useCallback((error) => {
    toast.error(
      error?.response?.data?.message ||
        'Could not read that document. Please enter the details manually.'
    );
    setStage(null);
  }, []);

  const runDocument = useCallback(
    async (docType, file) => {
      if (!file) return;
      setStage('send');
      try {
        setStage('extract');
        handleResult(await draftLandFromDocument(file, docType));
      } catch (error) {
        handleFailure(error);
      }
    },
    [handleResult, handleFailure]
  );

  const runDescription = useCallback(async () => {
    if (!description.trim()) {
      toast.error('Describe the land first.');
      return;
    }
    setStage('send');
    try {
      setStage('extract');
      handleResult(await draftLandFromDescription(description.trim()));
    } catch (error) {
      handleFailure(error);
    }
  }, [description, handleResult, handleFailure]);

  const apply = useCallback(() => {
    if (!draft) return;
    // Apply the parcels as the operator left them, not as the model returned them.
    onApplyDraft?.({ ...draft, khasra: khasraRows });
    toast.success('Applied. Every filled value is editable — please check them against the document.');
    reset();
  }, [draft, khasraRows, onApplyDraft, reset]);

  const stepState = (key) => {
    if (stage === 'review') return 'done';
    const order = STEPS.map((step) => step.key);
    const current = order.indexOf(stage);
    const mine = order.indexOf(key);
    if (current < 0) return 'idle';
    if (mine < current) return 'done';
    if (mine === current) return 'active';
    return 'idle';
  };

  const extractionRan = lastResult && lastResult.mode !== 'disabled' && lastResult.mode !== 'failed';

  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-4">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm text-stone-800">✨ Smart Fill</span>
        <span className="text-xs text-stone-500">Optional — you can type everything yourself</span>
      </div>

      <textarea
        className="mt-3 w-full rounded border border-stone-300 p-2 text-sm"
        rows={2}
        disabled={disabled || isBusy}
        placeholder="Describe the land. Example: 2.5 hectares off the Bhopal–Indore highway in Huzur tehsil, Bhopal district…"
        value={description}
        onChange={(event) => setDescription(event.target.value)}
      />
      <p className="mt-1 text-xs text-stone-500">
        A description can suggest the name and location only. Khasra numbers, areas, owner
        PANs and registry details are read from documents, never from prose.
      </p>

      <div className="mt-3 flex justify-end">
        <Button onClick={runDescription} disabled={disabled || isBusy || !description.trim()}>
          {isBusy ? 'Working…' : 'Generate'}
        </Button>
      </div>

      <div className="my-3 flex items-center gap-3">
        <span className="h-px flex-1 bg-stone-200" />
        <span className="text-xs uppercase tracking-wide text-stone-400">
          or extract from a document
        </span>
        <span className="h-px flex-1 bg-stone-200" />
      </div>

      <div className="grid grid-cols-2 gap-2">
        {LAND_SOURCE_DOCUMENTS.map((document) => (
          <label
            key={document.value}
            htmlFor={`land-smartfill-${document.value}`}
            title={document.description}
            className={`relative flex cursor-pointer items-center gap-2 rounded border px-3 py-2 text-xs ${
              disabled || isBusy
                ? 'border-stone-200 bg-stone-100 text-stone-400'
                : 'border-stone-300 bg-white text-stone-700 hover:bg-stone-50'
            }`}>
            <input
              id={`land-smartfill-${document.value}`}
              type="file"
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              accept=".pdf,.png,.jpg,.jpeg"
              disabled={disabled || isBusy}
              onChange={(event) => runDocument(document.value, event.target.files?.[0])}
            />
            {document.value === 'registry_deed' ? <FiFileText /> : <FiUpload />}
            {document.label}
          </label>
        ))}
      </div>

      {/*
        Hidden once we know AI is unavailable: a list of green ticks reads as "extraction
        ran and succeeded", which is the opposite of what happened.
      */}
      {stage !== null && extractionRan && (
        <ul className="mt-4 flex flex-col gap-1.5">
          {STEPS.map((step) => {
            const state = stepState(step.key);
            return (
              <li key={step.key} className="flex items-center gap-2 text-xs">
                {state === 'done' ? (
                  <FiCheck className="text-emerald-600" />
                ) : state === 'active' ? (
                  <FiLoader className="animate-spin text-emerald-600" />
                ) : (
                  <span className="h-3 w-3 rounded-full border border-stone-300" />
                )}
                <span className={state === 'idle' ? 'text-stone-400' : 'text-stone-700'}>
                  {step.label}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {stage === 'review' && extractionRan && (
        <div className="mt-4 rounded border border-stone-200 bg-white p-3">
          <p className="text-sm text-stone-800">Check before applying</p>
          <p className="mt-0.5 text-xs text-stone-500">
            These values were transcribed from your document by AI. Nothing has been
            checked against any government record — confirm each one against the document
            itself.
          </p>

          <CoverageNote coverage={lastResult.coverage} />

          {fieldEntries.length > 0 && (
            <ul className="mt-3 flex flex-col gap-1">
              {fieldEntries.map(([name, field]) => (
                <li key={name} className="flex items-baseline justify-between gap-3 text-xs">
                  <span className="text-stone-500">{LAND_DRAFT_FIELD_LABELS[name]}</span>
                  <span className="flex-1 text-right text-stone-800">
                    {field.value}
                    <span className={`ml-2 ${confidenceTone(field.confidence ?? 0)}`}>
                      {LAND_DOCUMENT_LABELS[field.source] || field.source}
                      {field.confidence != null && ` · ${Math.round(field.confidence * 100)}%`}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}

          {(draft?.owners || []).length > 0 && (
            <p className="mt-3 text-xs text-stone-600">
              {draft.owners.length} owner{draft.owners.length === 1 ? '' : 's'}:{' '}
              {draft.owners.map((owner) => owner.name).join(', ')}
            </p>
          )}

          <KhasraReviewGrid rows={khasraRows} onChange={setKhasraRows} />

          {(draft?.warnings || []).length > 0 && (
            <div className="mt-3 rounded border border-amber-300 bg-amber-50 px-3 py-2">
              {draft.warnings.map((warning) => (
                <p key={warning} className="text-xs text-amber-800">
                  {warning}
                </p>
              ))}
            </div>
          )}

          <div className="mt-3 flex items-center justify-end gap-2">
            <button
              type="button"
              className="text-xs text-stone-500 underline"
              onClick={reset}>
              Discard
            </button>
            <Button onClick={apply} disabled={disabled}>
              Apply to form
            </Button>
          </div>
        </div>
      )}

      {lastResult?.mode === 'disabled' && (
        <p className="mt-3 text-xs text-stone-500">
          AI assist is switched off. Every field below can be filled in by hand as usual.
        </p>
      )}

      {lastResult?.mode === 'failed' && (
        <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2">
          <p className="text-xs text-red-800">{lastResult.error || 'Extraction failed.'}</p>
          <p className="mt-0.5 text-xs text-red-700">
            AI assist is on, but this attempt did not complete. Fill the fields below by
            hand, or try another document.
          </p>
        </div>
      )}
    </div>
  );
};

export default LandSmartFillPanel;
